import { uniqueId, zipObject } from 'lodash';
import { isDefined, isOfType } from '../../../../ts-utils';
import { ReplySortOrder, Fieldset, } from '../../../../controller/services/comments-service';
import { getNextReplyChunkState, getPrevReplyChunkState, getPrevChunkState, getNextChunkState, } from './comments-paging-selectors';
import { biChangeOrdering } from '../../actions/bi-actions';
import { shouldFetchComments } from './comments-paging-watcher';
import { commentOrderingToSorting } from '../../../utils/ordering-to-sort';
import { getInitialFieldsets, getPageFieldsets } from './comment-request-utils';
import { getMessageFromError } from '../comments-errors';
import { ensureResponseBelongToResource, resolvePagination } from './comments-paging-utils';
export const FETCH_COMMENTS_REQUEST = 'FETCH_COMMENTS_REQUEST';
export const FETCH_COMMENTS_SUCCESS = 'FETCH_COMMENTS_SUCCESS';
export const FETCH_COMMENTS_FAILURE = 'FETCH_COMMENTS_FAILURE';
const REPLIES_SORTING = { order: ReplySortOrder.NEWEST_FIRST };
export const fetchCommentsRequest = (payload) => ({
    type: FETCH_COMMENTS_REQUEST,
    payload,
});
export const fetchCommentsSuccess = (payload) => ({
    type: FETCH_COMMENTS_SUCCESS,
    payload,
});
export const fetchCommentsFailure = (payload) => ({
    type: FETCH_COMMENTS_FAILURE,
    payload,
});
export function fetchComments({ resourceId, pagination: partialPagination, ctxFields, withVotes, withRatings, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState();
        const resourceState = state.comments[resourceId];
        const pagination = resolvePagination(partialPagination);
        // we want this function to be idempotent, because it might be called from controller/
        // from component/on classic pagination etc. and it could be done many times wiht same/equivalent pagination configs
        if (!shouldFetchComments(pagination, resourceState)) {
            return;
        }
        const requestId = uniqueId();
        dispatch(fetchCommentsRequest({
            resourceId,
            requestId,
            pagination,
            withVotes,
            withRatings,
        }));
        const result = await commentsService.fetchComments({
            fieldsets: getInitialFieldsets({
                withVotes,
                withRatings,
                fetchMode: state.metaState.fetchMode,
            }),
            request: {
                appId: appDefinitionId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                replySort: REPLIES_SORTING,
                resourceId,
                commentSort: commentOrderingToSorting({ ordering: pagination.ordering }),
                cursorPaging: {
                    limit: pagination.initialPage.commentLimit,
                    repliesLimit: pagination.initialPage.replyLimit,
                },
            },
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchCommentsSuccess({
                response: result.response,
                resourceId,
                requestId,
                pagination,
                ctxFields,
                fetchMode: state.metaState.fetchMode,
            }))
            : dispatch(fetchCommentsFailure({ resourceId, requestId }));
    };
}
export const BULK_FETCH_COMMENTS_REQUEST = 'BULK_FETCH_COMMENTS_REQUEST';
export const BULK_FETCH_COMMENTS_SUCCESS = 'BULK_FETCH_COMMENTS_SUCCESS';
export const BULK_FETCH_COMMENTS_FAILURE = 'BULK_FETCH_COMMENTS_FAILURE';
export const bulkFetchCommentsRequest = (payload) => ({
    type: BULK_FETCH_COMMENTS_REQUEST,
    payload,
});
export const bulkFetchCommentsSuccess = (payload) => ({
    type: BULK_FETCH_COMMENTS_SUCCESS,
    payload,
});
export const bulkFetchCommentsFailure = (payload) => ({
    type: BULK_FETCH_COMMENTS_FAILURE,
    payload,
});
export function bulkFetchComments(payload) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState();
        const commentsToFetch = payload
            .map((p) => ({
            ...p,
            pagination: resolvePagination(p.pagination),
        }))
            .filter(({ resourceId, pagination }) => {
            const resourceState = state.comments[resourceId];
            return shouldFetchComments(pagination, resourceState);
        });
        if (commentsToFetch.length === 0) {
            return;
        }
        const requestId = uniqueId();
        dispatch(bulkFetchCommentsRequest(commentsToFetch.map(({ resourceId, pagination, withVotes, withRatings }) => ({
            resourceId,
            requestId,
            pagination,
            withVotes,
            withRatings,
        }))));
        const [withVotes, withRatings] = payload.reduce((acc, curr) => {
            acc[0] = acc[0] || curr.withVotes;
            acc[1] = acc[0] || curr.withRatings;
            return acc;
        }, [false, false]);
        const result = await commentsService.bulkFetchComments({
            fieldsets: getInitialFieldsets({
                withVotes,
                withRatings,
                fetchMode: state.metaState.fetchMode,
            }),
            requests: commentsToFetch.map(({ resourceId, pagination, ctxFields }) => ({
                appId: appDefinitionId,
                resourceId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                repliesSorting: REPLIES_SORTING,
                commentSort: commentOrderingToSorting({ ordering: pagination.ordering }),
                cursorPaging: {
                    repliesLimit: pagination.initialPage.replyLimit,
                    limit: pagination.initialPage?.commentLimit,
                },
            })),
        });
        return result.type === 'SUCCESS'
            ? dispatch(bulkFetchCommentsSuccess({
                response: result.response,
                requestId,
                commentsByResourceId: result.response.commentsByResourceId.map((payload, index) => ({
                    resourceId: commentsToFetch[index].resourceId,
                    pagination: commentsToFetch[index].pagination,
                    ctxFields: commentsToFetch[index].ctxFields,
                    comments: payload.comments,
                    votes: payload.votes,
                    ratings: payload.ratings,
                    currentRating: payload.currentRating,
                    permissions: payload.permissions,
                    totalComments: payload.totalComments,
                    reactions: payload.reactions,
                })),
                fetchMode: state.metaState.fetchMode,
            }))
            : dispatch(bulkFetchCommentsFailure(commentsToFetch.map(({ resourceId }) => ({ resourceId, requestId }))));
    };
}
export const FETCH_DEEP_LINK_REQUEST = 'FETCH_DEEP_LINK_REQUEST';
export const FETCH_DEEP_LINK_SUCCESS = 'FETCH_DEEP_LINK_SUCCESS';
export const FETCH_DEEP_LINK_FAILURE = 'FETCH_DEEP_LINK_FAILURE';
export const fetchDeepLinkRequest = (payload) => ({
    type: FETCH_DEEP_LINK_REQUEST,
    payload,
});
export const fetchDeepLinkSuccess = (payload) => ({
    type: FETCH_DEEP_LINK_SUCCESS,
    payload,
});
export const fetchDeepLinkFailure = (payload) => ({
    type: FETCH_DEEP_LINK_FAILURE,
    payload,
});
export function fetchDeepLink({ resourceId, pagination: partialPagination, commentId, withVotes, withRatings, ctxFields, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState();
        const resourceState = state.comments[resourceId];
        if (resourceState?.type === 'READY' || resourceState?.type === 'PENDING') {
            // since this can be triggered from component or controller, we don't want to fetch twice
            return;
        }
        const pagination = resolvePagination(partialPagination);
        const requestId = uniqueId();
        dispatch(fetchDeepLinkRequest({
            resourceId,
            requestId,
            pagination,
            withVotes,
            withRatings,
        }));
        const result = await commentsService.fetchCommentThread({
            fieldsets: getInitialFieldsets({
                withVotes,
                withRatings,
                fetchMode: state.metaState.fetchMode,
            }),
            request: {
                appId: appDefinitionId,
                commentId,
                commentSort: commentOrderingToSorting({ ordering: pagination.ordering }),
                replySort: REPLIES_SORTING,
            },
        });
        if (result.type === 'SUCCESS') {
            const r = ensureResponseBelongToResource({
                response: result.response,
                resourceId,
            });
            if (r.type === 'ERROR') {
                return dispatch(fetchDeepLinkFailure({
                    resourceId,
                    requestId,
                    error: {
                        type: 'SERVER',
                        status: 404,
                        description: 'Comment not found',
                        applicationErrorCode: '',
                        baseError: new Error(),
                    },
                }));
            }
            dispatch(fetchDeepLinkSuccess({
                response: r.response,
                commentId,
                resourceId,
                requestId,
                pagination,
                ctxFields,
                fetchMode: state.metaState.fetchMode,
            }));
        }
        else {
            dispatch(fetchDeepLinkFailure({
                resourceId,
                requestId,
                error: result.error,
            }));
        }
    };
}
export const FETCH_NEXT_COMMENTS_REQUEST = 'FETCH_NEXT_COMMENTS_REQUEST';
export const FETCH_NEXT_COMMENTS_SUCCESS = 'FETCH_NEXT_COMMENTS_SUCCESS';
export const FETCH_NEXT_COMMENTS_FAILURE = 'FETCH_NEXT_COMMENTS_FAILURE';
export const fetchNextCommentsRequest = (payload) => ({
    type: FETCH_NEXT_COMMENTS_REQUEST,
    payload,
});
export const fetchNextCommentsSuccess = (payload) => ({
    type: FETCH_NEXT_COMMENTS_SUCCESS,
    payload,
});
export const fetchNextCommentsFailure = (payload) => ({
    type: FETCH_NEXT_COMMENTS_FAILURE,
    payload,
});
export function fetchNextComments({ resourceId, ctxFields, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState().comments[resourceId];
        if (state?.type !== 'READY') {
            return;
        }
        const nextChunkState = getNextChunkState(state);
        if (nextChunkState.type !== 'HAS') {
            return;
        }
        const { pagination } = state.configs;
        const requestId = uniqueId();
        dispatch(fetchNextCommentsRequest({ resourceId, requestId }));
        const result = await commentsService.fetchComments({
            fieldsets: getPageFieldsets({
                withVotes: state.configs.withVotes,
                withRatings: state.configs.withRatings,
            }),
            request: {
                appId: appDefinitionId,
                resourceId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                replySort: REPLIES_SORTING,
                cursorPaging: {
                    cursor: nextChunkState.cursor,
                    limit: pagination.pagination.commentLimit,
                    repliesLimit: pagination.pagination.replyLimit,
                },
            },
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchNextCommentsSuccess({ response: result.response, resourceId, requestId }))
            : dispatch(fetchNextCommentsFailure({
                resourceId,
                requestId,
                message: getMessageFromError(result.error),
            }));
    };
}
export const FETCH_PREV_COMMENTS_REQUEST = 'FETCH_PREV_COMMENTS_REQUEST';
export const FETCH_PREV_COMMENTS_SUCCESS = 'FETCH_PREV_COMMENTS_SUCCESS';
export const FETCH_PREV_COMMENTS_FAILURE = 'FETCH_PREV_COMMENTS_FAILURE';
export const fetchPrevCommentsRequest = (payload) => ({
    type: FETCH_PREV_COMMENTS_REQUEST,
    payload,
});
export const fetchPrevCommentsSuccess = (payload) => ({
    type: FETCH_PREV_COMMENTS_SUCCESS,
    payload,
});
export const fetchPrevCommentsFailure = (payload) => ({
    type: FETCH_PREV_COMMENTS_FAILURE,
    payload,
});
export function fetchPrevComments({ resourceId, ctxFields, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState().comments[resourceId];
        if (state?.type !== 'READY') {
            return;
        }
        const prevChunkState = getPrevChunkState(state);
        if (prevChunkState.type !== 'HAS') {
            return;
        }
        const { pagination } = state.configs;
        const requestId = uniqueId();
        dispatch(fetchPrevCommentsRequest({ resourceId, requestId }));
        const result = await commentsService.fetchComments({
            fieldsets: getPageFieldsets({
                withVotes: state.configs.withVotes,
                withRatings: state.configs.withRatings,
            }),
            request: {
                appId: appDefinitionId,
                resourceId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                cursorPaging: {
                    repliesLimit: pagination.pagination?.replyLimit,
                    cursor: prevChunkState.cursor,
                    limit: pagination.pagination?.commentLimit,
                },
            },
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchPrevCommentsSuccess({ response: result.response, resourceId, requestId }))
            : dispatch(fetchPrevCommentsFailure({
                resourceId,
                requestId,
                message: getMessageFromError(result.error),
            }));
    };
}
export const FETCH_NEXT_REPLIES_REQUEST = 'FETCH_NEXT_REPLIES_REQUEST';
export const FETCH_NEXT_REPLIES_SUCCESS = 'FETCH_NEXT_REPLIES_SUCCESS';
export const FETCH_NEXT_REPLIES_FAILURE = 'FETCH_NEXT_REPLIES_FAILURE';
const fetchNextRepliesRequest = (payload) => ({
    type: FETCH_NEXT_REPLIES_REQUEST,
    payload,
});
const fetchNextRepliesSuccess = (payload) => ({
    type: FETCH_NEXT_REPLIES_SUCCESS,
    payload,
});
const fetchNextRepliesFailure = (payload) => ({
    type: FETCH_NEXT_REPLIES_FAILURE,
    payload,
});
export function fetchNextReplies({ resourceId, commentId, ctxFields, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState().comments[resourceId];
        if (state?.type !== 'READY') {
            return;
        }
        const nextChunkState = getNextReplyChunkState(state.replyLists[commentId]);
        if (nextChunkState.type !== 'HAS') {
            return;
        }
        const { pagination } = state.configs;
        const requestId = uniqueId();
        dispatch(fetchNextRepliesRequest({ resourceId, commentId, requestId }));
        const result = await commentsService.fetchComments({
            fieldsets: getPageFieldsets({ withVotes: false, withRatings: false }),
            request: {
                appId: appDefinitionId,
                resourceId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                cursorPaging: {
                    repliesLimit: pagination.pagination?.replyLimit,
                    cursor: nextChunkState.cursor,
                    limit: pagination.replyShowMoreLimit,
                },
            },
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchNextRepliesSuccess({ response: result.response, resourceId, commentId, requestId }))
            : dispatch(fetchNextRepliesFailure({
                resourceId,
                commentId,
                requestId,
                message: getMessageFromError(result.error),
            }));
    };
}
export const FETCH_PREV_REPLIES_REQUEST = 'FETCH_PREV_REPLIES_REQUEST';
export const FETCH_PREV_REPLIES_SUCCESS = 'FETCH_PREV_REPLIES_SUCCESS';
export const FETCH_PREV_REPLIES_FAILURE = 'FETCH_PREV_REPLIES_FAILURE';
const fetchPrevRepliesRequest = (payload) => ({
    type: FETCH_PREV_REPLIES_REQUEST,
    payload,
});
const fetchPrevRepliesSuccess = (payload) => ({
    type: FETCH_PREV_REPLIES_SUCCESS,
    payload,
});
const fetchPrevRepliesFailure = (payload) => ({
    type: FETCH_PREV_REPLIES_FAILURE,
    payload,
});
export function fetchPrevReplies({ resourceId, commentId, ctxFields, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState().comments[resourceId];
        if (state?.type !== 'READY') {
            return;
        }
        const prevChunkState = getPrevReplyChunkState(state.replyLists[commentId]);
        if (prevChunkState.type !== 'HAS') {
            return;
        }
        const { pagination } = state.configs;
        const requestId = uniqueId();
        dispatch(fetchPrevRepliesRequest({ resourceId, commentId, requestId }));
        const result = await commentsService.fetchComments({
            fieldsets: getPageFieldsets({ withVotes: false, withRatings: false }),
            request: {
                appId: appDefinitionId,
                resourceId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                cursorPaging: {
                    cursor: prevChunkState.cursor,
                    limit: pagination.pagination?.commentLimit,
                    repliesLimit: pagination.pagination?.replyLimit,
                },
            },
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchPrevRepliesSuccess({ response: result.response, resourceId, commentId, requestId }))
            : dispatch(fetchPrevRepliesFailure({
                resourceId,
                commentId,
                requestId,
                message: getMessageFromError(result.error),
            }));
    };
}
export const changeOrdering = ({ resourceId, pagination: partialPagination, ctxFields, ordering, withVotes, withRatings, }) => {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const state = getState();
        const requestId = uniqueId();
        dispatch(biChangeOrdering({ sortBy: ordering, resourceId }));
        const pagination = {
            ...resolvePagination(partialPagination),
            ordering,
        };
        dispatch(fetchCommentsRequest({ resourceId, pagination, requestId, withVotes, withRatings }));
        const result = await commentsService.fetchComments({
            fieldsets: getInitialFieldsets({
                withVotes,
                withRatings,
                fetchMode: state.metaState.fetchMode,
            }),
            request: {
                resourceId,
                appId: appDefinitionId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                replySort: REPLIES_SORTING,
                commentSort: commentOrderingToSorting({ ordering: pagination.ordering }),
                cursorPaging: {
                    repliesLimit: pagination.pagination?.replyLimit,
                    limit: pagination.pagination?.commentLimit,
                },
            },
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchCommentsSuccess({
                response: result.response,
                resourceId,
                requestId,
                pagination,
                ctxFields,
                fetchMode: state.metaState.fetchMode,
            }))
            : dispatch(fetchCommentsFailure({ resourceId, requestId }));
    };
};
export const FETCH_INTERACTIONS_REQUEST = 'FETCH_INTERACTIONS_REQUEST';
export const FETCH_INTERACTIONS_FAILURE = 'FETCH_INTERACTIONS_FAILURE';
export const FETCH_INTERACTIONS_SUCCESS = 'FETCH_INTERACTIONS_SUCCESS';
const fetchInteractionsRequest = (payload) => ({
    type: FETCH_INTERACTIONS_REQUEST,
    payload,
});
const fetchInteractionsFailure = (payload) => ({
    type: FETCH_INTERACTIONS_FAILURE,
    payload,
});
const fetchInteractionsSuccess = (payload) => ({
    type: FETCH_INTERACTIONS_SUCCESS,
    payload,
});
export function fetchInteractions({ resourceId, ctxFields, withVotes, withRatings, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const resourceState = getState().comments[resourceId];
        if (resourceState?.type !== 'READY' || resourceState.interactionFetchState === 'fetched') {
            return;
        }
        dispatch(fetchInteractionsRequest({ resourceId }));
        const result = await commentsService.fetchCommentData({
            appId: appDefinitionId,
            contextId: ctxFields.contextId,
            contextType: ctxFields.contextType,
            resourceId,
            commentIds: Object.values(resourceState.commentsById)
                .map((c) => c.type === 'COMMENT_READY' || c.type === 'REPLY_READY' ? c.comment.commentId : undefined)
                .filter(isDefined),
            fieldsets: [
                Fieldset.REACTIONS,
                ...(withVotes ? [Fieldset.VOTES] : []),
                ...(withRatings ? [Fieldset.CURRENT_RATING] : []),
            ],
        });
        result.type === 'SUCCESS'
            ? dispatch(fetchInteractionsSuccess({ resourceId, response: result.response }))
            : dispatch(fetchInteractionsFailure({ resourceId }));
    };
}
export const REFETCH_COMMENTS_DATA_REQUEST = 'REFETCH_COMMENTS_DATA_REQUEST';
export const REFETCH_COMMENTS_DATA_SUCCESS = 'REFETCH_COMMENTS_DATA_SUCCESS';
export const REFETCH_COMMENTS_DATA_FAILURE = 'REFETCH_COMMENTS_DATA_FAILURE';
export const refetchCommentsDataRequest = () => ({
    type: REFETCH_COMMENTS_DATA_REQUEST,
    payload: {},
});
export const refetchCommentsDataSuccess = (payload) => ({
    type: REFETCH_COMMENTS_DATA_SUCCESS,
    payload,
});
export const refetchCommentsDataError = () => ({
    type: REFETCH_COMMENTS_DATA_FAILURE,
});
export function refetchCommentsData() {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const commentsState = getState().comments;
        dispatch(refetchCommentsDataRequest());
        const commentsResources = Object.keys(commentsState)
            .map((resourceId) => {
            const resourceCommentsState = commentsState[resourceId];
            if (resourceCommentsState?.type !== 'READY') {
                return undefined;
            }
            const { contextType, contextId } = resourceCommentsState.configs.ctxFields;
            return {
                resourceId,
                contextType,
                contextId,
                ordering: resourceCommentsState.configs.pagination.ordering,
            };
        })
            .filter(isDefined);
        const result = await commentsService.bulkFetchComments({
            requests: commentsResources.map(({ ordering, ...r }) => ({
                ...r,
                appId: appDefinitionId,
                commentSort: commentOrderingToSorting({ ordering }),
            })),
            fieldsets: [
                Fieldset.MEMBERS,
                Fieldset.CURRENT_MEMBER,
                Fieldset.MEMBER_BADGES,
                Fieldset.MEMBER_ROLES,
                Fieldset.CONTEXT_PERMISSIONS,
                Fieldset.VOTES,
            ],
            skipPermissionCache: true,
        });
        if (result.type === 'SUCCESS') {
            const commentsDataByResourceId = zipObject(commentsResources.map((r) => r?.resourceId).filter(isDefined), result.response.commentsByResourceId);
            dispatch(refetchCommentsDataSuccess({
                response: result.response,
                commentsDataByResourceId,
            }));
        }
        else {
            dispatch(refetchCommentsDataError());
        }
    };
}
export const VIEW_REPLY_TARGET_DELETED = 'VIEW_REPLY_TARGET_DELETED';
export const VIEW_REPLY_TARGET_AVAILABLE = 'VIEW_REPLY_TARGET_AVAILABLE';
export const VIEW_REPLY_TARGET_REQUEST = 'VIEW_REPLY_TO_REQUEST';
export const VIEW_REPLY_TARGET_SUCCESS = 'VIEW_REPLY_TO_SUCCESS';
export const VIEW_REPLY_TARGET_FAILURE = 'VIEW_REPLY_TO_FAILURE';
export const viewReplyTargetDeleted = (payload) => ({
    type: VIEW_REPLY_TARGET_DELETED,
    payload,
});
export const viewReplyTargetAvailable = (payload) => ({
    type: VIEW_REPLY_TARGET_AVAILABLE,
    payload,
});
export const viewReplyTargetRequest = (payload) => ({
    type: VIEW_REPLY_TARGET_REQUEST,
    payload,
});
export const viewReplyTargetFailure = (payload) => ({
    type: VIEW_REPLY_TARGET_FAILURE,
    payload,
});
export const viewReplyTargetSuccess = (payload) => ({
    type: VIEW_REPLY_TARGET_SUCCESS,
    payload,
});
export function viewReplyTarget({ resourceId, targetCommentId, sourceCommentId, parentCommentId, ctxFields, targetStatus, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId }) => {
        const commentsState = getState().comments[resourceId];
        if (commentsState?.type !== 'READY') {
            return;
        }
        const commentState = commentsState.commentsById[targetCommentId];
        if (commentState) {
            return dispatch(viewReplyTargetAvailable({ resourceId, targetCommentId }));
        }
        if (targetStatus === 'DELETED') {
            return dispatch(viewReplyTargetDeleted({ resourceId, sourceCommentId, targetCommentId, parentCommentId }));
        }
        dispatch(viewReplyTargetRequest({ resourceId, targetCommentId, sourceCommentId }));
        const result = await commentsService.getBulkComments({
            appId: appDefinitionId,
            contextId: ctxFields.contextId,
            contextType: ctxFields.contextType,
            resourceId,
            fieldsets: [
                Fieldset.MEMBERS,
                Fieldset.MEMBER_BADGES,
                Fieldset.MEMBER_ROLES,
                Fieldset.REACTIONS,
                Fieldset.MEMBERS,
                Fieldset.VOTES,
            ],
            commentIds: [targetCommentId],
        });
        if (result.type === 'SUCCESS') {
            const comment = result.response?.comments?.find((c) => c.commentId === targetCommentId);
            comment
                ? dispatch(viewReplyTargetSuccess({
                    resourceId,
                    targetCommentId,
                    sourceCommentId,
                    comment,
                    members: result.response.members ?? [],
                    memberBadges: result.response.memberBadges ?? {},
                    memberRoles: result.response.memberRoles ?? {},
                    reactions: result.response.reactions ?? {},
                }))
                : dispatch(viewReplyTargetFailure({
                    resourceId,
                    targetCommentId,
                    sourceCommentId,
                    message: getMessageFromError({
                        type: 'SERVER',
                        status: undefined,
                        description: undefined,
                        applicationErrorCode: undefined,
                        baseError: new Error('Comment not found'),
                    }),
                }));
        }
        else {
            dispatch(viewReplyTargetFailure({
                resourceId,
                targetCommentId,
                sourceCommentId,
                message: getMessageFromError(result.error),
            }));
        }
    };
}
export const UNMOUNT_ALL_RESOURCES = 'UNMOUNT_ALL_RESOURCES';
export const unmountAllResources = () => ({
    type: UNMOUNT_ALL_RESOURCES,
});
export const isUnmountAllResourcesAction = isOfType([UNMOUNT_ALL_RESOURCES]);
export const isCommentsFetchAction = isOfType([FETCH_COMMENTS_SUCCESS, FETCH_COMMENTS_REQUEST, FETCH_COMMENTS_FAILURE]);
export const isDeepLinkFetchAction = isOfType([FETCH_DEEP_LINK_SUCCESS, FETCH_DEEP_LINK_REQUEST, FETCH_DEEP_LINK_FAILURE]);
export const isCommentsPagingAction = isOfType([
    FETCH_NEXT_COMMENTS_REQUEST,
    FETCH_NEXT_COMMENTS_SUCCESS,
    FETCH_NEXT_COMMENTS_FAILURE,
    FETCH_PREV_COMMENTS_REQUEST,
    FETCH_PREV_COMMENTS_SUCCESS,
    FETCH_PREV_COMMENTS_FAILURE,
    FETCH_NEXT_REPLIES_REQUEST,
    FETCH_NEXT_REPLIES_SUCCESS,
    FETCH_NEXT_REPLIES_FAILURE,
    FETCH_PREV_REPLIES_REQUEST,
    FETCH_PREV_REPLIES_SUCCESS,
    FETCH_PREV_REPLIES_FAILURE,
    VIEW_REPLY_TARGET_DELETED,
    VIEW_REPLY_TARGET_AVAILABLE,
    VIEW_REPLY_TARGET_REQUEST,
    VIEW_REPLY_TARGET_SUCCESS,
    VIEW_REPLY_TARGET_FAILURE,
    FETCH_INTERACTIONS_REQUEST,
    FETCH_INTERACTIONS_FAILURE,
    FETCH_INTERACTIONS_SUCCESS,
]);
//# sourceMappingURL=comments-paging-actions.js.map